import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { upload } from 'src/helpers/upLoadHelper';
import {
  ValidationErrors,
  EMAIL_REGEX,
  PHONE_REGEX,
  USER_ROLE_NAMES,
  CompanySettings,
} from '../types';
import { toast } from 'react-toastify';
import {
  getUserProfileDetails,
  updateCompanyDetails,
} from 'src/services/client';

const initialCompanySettings: CompanySettings = {
  companyName: '',
  companyAddress: '',
  companyCity: '',
  companyState: '',
  companyZip: '',
  companyEmail: '',
  companyPhone: '',
  companyLogo: '',
  selectedCompanyLogo: null,
};

export const useCompanySettings = () => {
  const dispatch = useDispatch();
  const user: any = useSelector(selectAuthenticatedUser);

  const [settings, setSettings] = useState<CompanySettings>(initialCompanySettings);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saveClicked, setSaveClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const userRoleName: any = user?.roles?.[0]?.role?.name;
  const isPropertyDeveloper: boolean = userRoleName === USER_ROLE_NAMES.PROPERTY_DEVELOPER;

  useEffect(() => {
    const fetchLatestData = async () => {
      if (!user?.id || !isPropertyDeveloper) return;

      try {
        const response: any = await getUserProfileDetails(user.id);
        if (response.status === 200 || response.ok) {
          const json = await response.json();

          if (json) {
            setSettings({
              companyName: json.companyName || '',
              companyAddress: json.companyAddress || '',
              companyCity: json.companyCity || '',
              companyState: json.companyState || '',
              companyZip: json.companyZip || '',
              companyEmail: json.companyEmail || '',
              companyPhone: json.companyPhone || '',
              companyLogo: json.companyLogo || '',
              selectedCompanyLogo: null,
            });
          }
        }
      } catch (error) {
        console.error('Failed to sync company settings:', error);
      }
    };

    fetchLatestData();
  }, [user?.id, isPropertyDeveloper]);

  const clearSaveState = useCallback(() => {
    setSaveClicked(false);
  }, []);

  const updateField = useCallback(
    (field: keyof CompanySettings, value: any) => {
      setSettings((prev: CompanySettings) => ({
        ...prev,
        [field]: value,
      }));
    },
    [dispatch]
  );

  const validateForm = (): ValidationErrors => {
    const validationErrors: ValidationErrors = {};

    if (!settings?.companyName?.trim()) {
      validationErrors.companyName = 'Please Enter Company Name !';
      validationErrors.errors = true;
    }

    if (!settings?.companyAddress?.trim()) {
      validationErrors.companyAddress = 'Please Enter Company Address !';
      validationErrors.errors = true;
    }

    if (!settings?.companyEmail?.trim()) {
      validationErrors.companyEmail = 'Please Enter Company Email !';
      validationErrors.errors = true;
    } else if (!EMAIL_REGEX.test(settings.companyEmail.toLowerCase())) {
      validationErrors.companyEmail = 'Please Enter Valid Company Email !';
      validationErrors.errors = true;
    }

    if (!settings?.companyPhone || settings.companyPhone.length < 10) {
      validationErrors.companyPhone = 'Please Enter a 10 digit Phone Number';
      validationErrors.errors = true;
    } else if (settings.companyPhone.length > 20) {
      validationErrors.companyPhone = 'Please Enter a Valid Phone Number';
      validationErrors.errors = true;
    } else if (!settings.companyPhone.match(PHONE_REGEX)) {
      validationErrors.companyPhone = 'Please Enter a Valid Phone Number';
      validationErrors.errors = true;
    }

    return validationErrors;
  };

  const handleSubmit = async (): Promise<boolean> => {
    const validationErrors: ValidationErrors = validateForm();

    setErrors(validationErrors);
    setSaveClicked(true);

    if (validationErrors.errors) {
      return false;
    }

    setIsLoading(true);

    try {
      let logoUrl = settings.companyLogo;

      if (settings?.selectedCompanyLogo?.length === 1) {
        logoUrl = await upload(settings.selectedCompanyLogo[0]);
      }

      const data = {
        userId: user.id,
        companyName: settings.companyName,
        companyAddress: settings.companyAddress,
        companyCity: settings.companyCity,
        companyState: settings.companyState,
        companyZip: settings.companyZip,
        companyEmail: settings.companyEmail,
        companyPhone: settings.companyPhone,
        companyLogo: logoUrl,
      };

      const response: any = await updateCompanyDetails(data);

      if (response.status === 200 || response.ok) {
        const responseJson = await response.json();

        setSettings((prev: CompanySettings) => ({
          ...prev,
          ...responseJson.data,
          selectedCompanyLogo: null,
        }));

        toast.success('ⓘ Company details updated.', {
          autoClose: 10000,
          position: 'top-center',
        });

        return true;
      } else {
        toast.error("Couldn't update company details.", {
          autoClose: 10000,
          position: 'top-center',
        });
        return false;
      }
    } catch (error) {
      console.error('Failed to update company details:', error);
      toast.error('Something went wrong.', {
        autoClose: 10000,
        position: 'top-center',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    settings,
    errors,
    saveClicked,
    isLoading,
    isPropertyDeveloper,
    updateField,
    handleSubmit,
    clearSaveState,
  };
};